/* eslint-disable no-shadow */
import React, { useEffect, useState } from 'react';
import { CancelButton, ConfirmButton } from '~/components/Buttons';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { Col, Form, Modal, Row } from 'react-bootstrap';
import {
  MdArrowDownward,
  MdArrowUpward,
  MdKeyboardArrowDown,
  MdKeyboardArrowUp,
} from 'react-icons/md';
import {
  Container,
  ContainerItem,
  List,
  ListaItem,
  Controller,
  Button,
} from './styles';
import { useModulo } from '../../hooks/useModulo';
import { subModuloProps } from '../../types';
import { addUpdate } from '~/utils/masterDetail';

export const OrderSubModulo: React.FC = () => {
  const [selected, setSelected] = useState<string>();
  const [localRows, setLocalRows] = useState<subModuloProps[]>([]);
  const [blockButtons, setBlockButtons] = useState<{
    top: boolean;
    up: boolean;
    down: boolean;
    bottom: boolean;
  }>({
    top: false,
    up: false,
    down: false,
    bottom: false,
  });
  const {
    rows,
    setRows,
    openOrderModal,
    handleOpenOrderModal,
    masterDetail,
    setMasterDetail,
    setLoadingTable,
  } = useModulo();

  useEffect(() => {
    if (openOrderModal) {
      setLocalRows([...rows]);
      setSelected(undefined);
    }
  }, [openOrderModal]);

  useEffect(() => {
    if (!selected) {
      setBlockButtons({
        top: true,
        up: true,
        down: true,
        bottom: true,
      });
      return;
    }

    const index = localRows.findIndex((i) => i.uuid === selected);
    const isFirst = index === 0;
    const isLast = index === localRows.length - 1;

    setBlockButtons({
      top: isFirst,
      up: isFirst,
      down: isLast,
      bottom: isLast,
    });
  }, [selected, localRows]);

  function handleCampo(uuid: string): void {
    setSelected(uuid);
  }

  const handleCloseModal = () => {
    handleOpenOrderModal(false);
  };

  const updateOrderNumbers = (list: any[]) =>
    list.map((item, index) => ({
      ...item,
      num_ordem_menu: index + 1,
    }));

  const handleOnDragEnd = (result: any) => {
    if (!result.destination) return;

    const items = Array.from(localRows);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const updated = updateOrderNumbers(items);
    setLocalRows(updated);
  };

  const moveItem = (direction: 'up' | 'down' | 'top' | 'bottom') => {
    if (!selected) return;

    const index = localRows.findIndex((i) => i.uuid === selected);
    if (index === -1) return;

    if ((direction === 'up' || direction === 'top') && index === 0) return;

    if (
      (direction === 'down' || direction === 'bottom') &&
      index === localRows.length - 1
    )
      return;

    const updated = [...localRows];
    const [movedItem] = updated.splice(index, 1);

    switch (direction) {
      case 'up':
        updated.splice(index - 1, 0, movedItem);
        break;
      case 'down':
        updated.splice(index + 1, 0, movedItem);
        break;
      case 'top':
        updated.unshift(movedItem);
        break;
      case 'bottom':
        updated.push(movedItem);
        break;
      default:
        break;
    }

    const finalList = updateOrderNumbers(updated);
    setLocalRows(finalList);
    setSelected(movedItem.uuid);
  };

  const handleConfirm = () => {
    setLoadingTable(true);
    addUpdate('subModulos', localRows, masterDetail, setMasterDetail);
    setRows(localRows);
    handleCloseModal();
    setLoadingTable(false);
  };

  return (
    <Modal
      size="lg"
      show={openOrderModal}
      onHide={handleCloseModal}
      centered
      animation
    >
      <Modal.Header>
        <Modal.Title>Ordenação dos Submódulos</Modal.Title>
        <button onClick={handleCloseModal} type="button" className="btn-close">
          {}
        </button>
      </Modal.Header>
      <Modal.Body>
        <Container>
          <Row>
            <Col xs={12}>
              <DragDropContext onDragEnd={handleOnDragEnd}>
                <ContainerItem>
                  <Form.Label className="title">
                    Submódulos disponíveis
                  </Form.Label>
                  <List>
                    <Droppable droppableId="selecionados" key="2">
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          style={{
                            background: snapshot.isDraggingOver
                              ? '#dfe1e6'
                              : '',
                            height: 'auto',
                          }}
                        >
                          {localRows.map((item, index) => (
                            <Draggable
                              draggableId={`${item.uuid}`}
                              key={item.uuid}
                              index={index}
                            >
                              {(provided) => (
                                <div
                                  id={`${item.uuid}`}
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                >
                                  <ListaItem
                                    onPointerDownCapture={() =>
                                      handleCampo(item.uuid)
                                    }
                                    className={
                                      selected === item.uuid
                                        ? 'selected'
                                        : 'unselected'
                                    }
                                    style={{
                                      padding: '0px 16px',
                                    }}
                                  >
                                    {item.des_modulo_secao}
                                  </ListaItem>
                                </div>
                              )}
                            </Draggable>
                          ))}
                        </div>
                      )}
                    </Droppable>
                  </List>
                  <Controller>
                    <Button
                      type="button"
                      isBlocked={blockButtons.top}
                      disabled={blockButtons.top}
                      onClick={() => moveItem('top')}
                    >
                      <MdArrowUpward />
                    </Button>
                    <Button
                      type="button"
                      isBlocked={blockButtons.up}
                      disabled={blockButtons.up}
                      onClick={() => moveItem('up')}
                    >
                      <MdKeyboardArrowUp />
                    </Button>
                    <Button
                      type="button"
                      isBlocked={blockButtons.down}
                      disabled={blockButtons.down}
                      onClick={() => moveItem('down')}
                    >
                      <MdKeyboardArrowDown />
                    </Button>
                    <Button
                      type="button"
                      isBlocked={blockButtons.bottom}
                      disabled={blockButtons.bottom}
                      onClick={() => moveItem('bottom')}
                    >
                      <MdArrowDownward />
                    </Button>
                  </Controller>
                </ContainerItem>
              </DragDropContext>
            </Col>
          </Row>
          <Row className="mt-2">
            <Col xs={12}>
              <div className="d-flex align-items-center justify-content-end">
                <CancelButton onClick={handleCloseModal}>Cancelar</CancelButton>
                <ConfirmButton onClick={handleConfirm}>Confirmar</ConfirmButton>
              </div>
            </Col>
          </Row>
        </Container>
      </Modal.Body>
    </Modal>
  );
};
